import {
	CreateTransportEventRequest,
	TransportEvent,
	TransportEventDriver,
	TransportEventModalData,
} from '@/new-transport/transport-timetable/transport-event/transport-event.model';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatNativeDateModule, MatOption } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInput, MatInputModule } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, finalize, map, of, switchMap } from 'rxjs';
import {
	ResourceName,
	TransportEventApiService,
} from '@/new-transport/transport-timetable/_api/transport-event.api.service';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GenericMatAutocompleteComponent } from '@/common/generic-mat-autocomplete/generic-mat-autocomplete.component';
import { environment } from 'src/environments/environment';

enum FormControlName {
	TOURISM_EVENT = 'tourismEventSearch',
	VEHICLE = 'vehicleSearch',
	DRIVER = 'driverSearch',
}

@Component({
	selector: 'app-create-new-transport-event-modal',
	standalone: true,
	imports: [
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatButton,
		ReactiveFormsModule,
		MatLabel,
		MatInput,
		MatFormField,
		MatButtonModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatOption,
		MatAutocomplete,
		MatAutocompleteTrigger,
		FormsModule,
		MatProgressSpinnerModule,
		GenericMatAutocompleteComponent,
	],
	templateUrl: './create-new-transport-event-modal.component.html',
	styleUrl: './create-new-transport-event-modal.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateNewTransportEventModalComponent implements OnInit {
	readonly data = inject<TransportEventModalData>(MAT_DIALOG_DATA);
	readonly FormControlName = FormControlName;

	readonly apiService = inject(TransportEventApiService);
	readonly infoBox = inject(InfoboxService);

	readonly eventsList = signal<TransportEvent[]>([]);
	readonly driversList = signal<TransportEventDriver[]>([]);
	readonly submitLoader = signal(false);

	readonly tourismEventForm: FormGroup = new FormGroup({
		[FormControlName.TOURISM_EVENT]: new FormControl('', [Validators.required, this.autocompleteObjectValidator()]),
		[FormControlName.DRIVER]: new FormControl('', [this.autocompleteObjectValidator()]),
		[FormControlName.VEHICLE]: new FormControl('', [Validators.required, this.autocompleteObjectValidator()]),
	});

	readonly environment = environment;
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialogRef = inject(MatDialogRef<CreateNewTransportEventModalComponent>);

	autocompleteObjectValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (control.value && typeof control.value === 'string') {
				return { invalidAutocompleteObject: true };
			}
			return null;
		};
	}

	autocompleteArrayValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;

			if (!Array.isArray(value)) {
				return { invalidAutocompleteArray: true };
			}

			if (value.length === 0) {
				return { required: true };
			}

			if (value.some((item) => typeof item === 'string')) {
				return { invalidAutocompleteObject: true };
			}

			return null;
		};
	}

	ngOnInit(): void {
		this.tourismEventForm.patchValue({ [FormControlName.VEHICLE]: this.data.vehicle });

		this.setupSearch(FormControlName.TOURISM_EVENT, ResourceName.TOURISM_EVENTS, this.eventsList);
		this.setupSearch(FormControlName.DRIVER, ResourceName.DRIVERS, this.driversList);
	}

	displayEventName(event: TransportEvent): string {
		return event ? `(${event.newId}) ${event.name}` : '';
	}

	displayDriverName(driver: TransportEventDriver): string {
		return driver ? `${driver.name} ${driver.surname}` : '';
	}

	onSubmit(): void {
		this.submitLoader.set(true);

		const tourismEventRequest: CreateTransportEventRequest = {
			antennaToTourismEventIds: this.tourismEventForm
				.get(FormControlName.TOURISM_EVENT)
				.value.map((item) => item.id),
			vehicleID: this.tourismEventForm.get(FormControlName.VEHICLE).value.id,
			driverID: this.tourismEventForm.get(FormControlName.DRIVER).value.id,
			tours: this.data.passengers,
			side: this.data.side,
		};

		this.createTourismEvent(tourismEventRequest);
	}

	onCancel(): void {
		this.dialogRef.close({ status: 'dialogClosed' });
	}

	erpEventsMapper = (response: string[]): string[] => {
		if (!Array.isArray(response)) {
			return [];
		}

		return response;
	};

	onTourismEventsSelected(events: TransportEvent[]): void {
		this.tourismEventForm.get(FormControlName.TOURISM_EVENT).setValue(events);
	}

	private setupSearch<T>(
		controlName: FormControlName,
		resourceName: ResourceName,
		resultSignal: ReturnType<typeof signal<T[]>>,
	): void {
		this.tourismEventForm
			.get(controlName)
			?.valueChanges.pipe(
				debounceTime(250),
				distinctUntilChanged(),
				switchMap((query: string | T) => {
					if (typeof query === 'string') {
						return this.apiService.search<T>(resourceName, query, this.data.side ?? null);
					}
					return of([]);
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((results: T[]) => {
				resultSignal.set(results);
			});
	}

	private createTourismEvent(tourismEventData: CreateTransportEventRequest): void {
		this.apiService
			.createTourismEvent(tourismEventData)
			.pipe(
				map((response) => {
					return `${window.location.origin}/tourism-events/${response.id}`;
				}),
				finalize(() => {
					this.submitLoader.set(false);
				}),
			)
			.subscribe((url: string | null) => {
				if (typeof url !== 'string') return;

				window.open(url, '_blank');
				this.dialogRef.close(null);

				this.infoBox.success('Pomyślnie utworzono wydarzenie antenkowe.');
			});
	}
}
